import { Ban } from 'lucide-react';
import { useEsgMandateScreens } from '../../hooks';
import { SecurityTypeClassification } from './SecurityTypeClassification';
import { LiquidityInvestability } from './LiquidityInvestability';
import { EsgMandateScreens } from './EsgMandateScreens';
import { useSmartBetaStrategyStore } from '@/features/Strategies/stores/definitionFormStore';
import type {
  useLiquidityFilters,
  useSecurityTypeClassification,
} from '../../hooks';

interface EligibilityRulesProps {
  securityType: ReturnType<typeof useSecurityTypeClassification>;
  liquidity: ReturnType<typeof useLiquidityFilters>;
}

// Figma's UniverseSelection.tsx (line 2570 onwards) renders "Eligibility
// & Exclusion Rules" as a single flat scroll with every sub-section
// open by default — Business Classifications, Geographic Classifications,
// the numerical Min/Max bands (Price / ADV / Free Float / Market Cap /
// Listing History), and Restriction Lists all stack vertically under one
// h4 with no expand/collapse chrome.
//
// The previous implementation here wrapped the three groupings
// (security_type / liquidity / esg) in a 3-row accordion that defaulted
// to collapsed, so a viewer had to know to click each row to see what
// was inside. The fields existed but were one click away each. This
// pass strips the accordion and renders all three sections flush — the
// content matches the export's flat layout end-to-end.
export function EligibilityRules({
  securityType,
  liquidity,
}: EligibilityRulesProps) {
  const esgMandate = useEsgMandateScreens();
  // Wire the Primary-Share-Class toggle to data_processor.primary_share — the
  // field the submission adapter actually serializes — so the toggle drives the
  // backtest, not just the preview count. (It previously fell back to a separate
  // StartingUniverse-store flag that was never submitted.)
  const primaryShare = useSmartBetaStrategyStore(
    (s) => s.data_processor.primary_share,
  );
  const setPrimaryShare = useSmartBetaStrategyStore((s) => s.setPrimaryShare);

  return (
    <div className="space-y-2.5 pt-3 border-t border-border/20">
      {/* Figma's h4 chrome — w-7 h-7 primary-tinted icon chip + 16px
          title + 14px muted description, copy hardcoded inline. */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Ban className="w-3.5 h-3.5 text-primary" />
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-[16px]">
            Eligibility &amp; Exclusion Rules
          </h4>
          <p className="text-muted-foreground text-[14px]">
            Constituent eligibility rules at each reconstitution
          </p>
        </div>
      </div>

      {/* All three sub-sections render flat. Figma UniverseSelection.tsx
          line 2577 spaces them with space-y-2.5 and the cards sit flush
          (the px-3/pt-1.5/pb-2.5 lives INSIDE each card, not around it),
          matching the tighter rhythm of the nested Liquidity sub-cards. */}
      <div className="space-y-2.5">
        <SecurityTypeClassification
          securityTypeItems={securityType.securityTypeItems}
          disabledSecurityTypes={securityType.disabledSecurityTypes}
          securityTypes={securityType.securityTypes}
          gics={securityType.gics}
          gicsSectors={securityType.gicsSectors}
          gicsIndustries={securityType.gicsIndustries}
          gicsSubIndustries={securityType.gicsSubIndustries}
          disabledGicsCodes={securityType.disabledGicsCodes}
          countries={securityType.countries}
          visibleCountries={securityType.visibleCountries}
          countriesNotInUniverse={securityType.countriesNotInUniverse}
          inUniverseSectorCodes={securityType.inUniverseSectorCodes}
          inUniverseIndustryCodes={securityType.inUniverseIndustryCodes}
          inUniverseSubIndustryCodes={securityType.inUniverseSubIndustryCodes}
          inUniverseCountries={securityType.inUniverseCountries}
          inUniverseMics={securityType.inUniverseMics}
          exchanges={securityType.exchanges}
          visibleExchanges={securityType.visibleExchanges}
          primaryShareClassExcludedCount={
            securityType.primaryShareClassExcludedCount
          }
          classificationExcludedCount={
            securityType.classificationExcludedCount
          }
          primaryShareClassOnly={primaryShare}
          onPrimaryShareClassChange={setPrimaryShare}
        />

        <LiquidityInvestability
          filters={liquidity.filters}
          update={liquidity.update}
          distributions={liquidity.distributions}
          filterExcludedCounts={liquidity.filterExcludedCounts}
        />

        <EsgMandateScreens esgMandate={esgMandate} />
      </div>
    </div>
  );
}
